import React from 'react';
import { Link } from 'react-router-dom';
import { Accessibility as AccessibilityIcon, ArrowLeft, Mail } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const AccessibilityPage = () => (
  <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white" data-testid="accessibility-page">
    <PageMeta
      slug="accessibility"
      path="/accessibility"
      title="Accessibility"
      description="HiBFF commits to WCAG 2.1 Level AA, the European Accessibility Act, and UK Public Sector Bodies Accessibility Regulations 2018."
    />
    <header className="px-6 sm:px-12 py-6 max-w-4xl mx-auto">
      <Link to="/" className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900" data-testid="accessibility-back-home">
        <ArrowLeft size={18} /> <span className="font-medium">HiBFF home</span>
      </Link>
    </header>

    <main className="px-6 sm:px-12 max-w-4xl mx-auto pb-20">
      <div className="flex items-center gap-3 mb-2 text-purple-600">
        <AccessibilityIcon size={28} />
        <span className="font-semibold text-sm uppercase tracking-wide">Accessibility</span>
      </div>
      <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ color: '#1A1209' }}>
        Accessibility statement
      </h1>
      <p className="text-base text-gray-700 mb-8">
        HiBFF is committed to providing a digital experience that is accessible to the widest
        possible audience, regardless of ability, technology, or context. We design, build,
        and test for compliance with{' '}
        <strong>WCAG 2.1 Level AA</strong>, the European Accessibility Act, and the UK
        Public Sector Bodies (Websites and Mobile Applications) Accessibility Regulations 2018.
      </p>

      <Section title="What we currently support">
        <ul className="space-y-2 text-gray-700">
          <li>· Keyboard navigation across all interactive elements</li>
          <li>· Visible focus indicators on every focusable control</li>
          <li>· Semantic HTML5 markup with ARIA labels on dynamic content</li>
          <li>· Colour contrast ratios meeting or exceeding 4.5:1 for body text</li>
          <li>· Screen-reader-friendly form fields with associated labels</li>
          <li>· Resizable text up to 200% without loss of content or function</li>
          <li>· Captions on AvA video where present; alt-text on images</li>
          <li>· No content that flashes more than three times per second</li>
        </ul>
      </Section>

      <Section title="What we are still improving">
        <ul className="space-y-2 text-gray-700">
          <li>· Full assistive-technology user-test pass on the mobile app (Android v1.0.4 — in progress)</li>
          <li>· Voice-only experience for AvA practice partners (in design)</li>
          <li>· High-contrast theme toggle (planned)</li>
        </ul>
      </Section>

      <Section title="Reporting an issue">
        <p className="text-gray-700">
          If you encounter an accessibility barrier on hibff.com, the HiBFF mobile app, or any
          email or document we send, please tell us. We aim to acknowledge accessibility
          reports within <strong>2 business days</strong> and resolve them within{' '}
          <strong>10 business days</strong> where reasonable.
        </p>
        <p className="mt-4">
          <a
            href="mailto:accessibility@hibff.com"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition text-sm font-semibold"
            data-testid="accessibility-email-link"
          >
            <Mail size={16} /> accessibility@hibff.com
          </a>
        </p>
      </Section>

      <Section title="Standards & evidence">
        <p className="text-gray-700">
          We test with: NVDA, VoiceOver, JAWS, axe DevTools, Lighthouse Accessibility audits,
          and manual keyboard-only walkthroughs. Audit reports are available on request to
          enterprise customers and procurement teams.
        </p>
      </Section>

      <p className="text-xs text-gray-500 mt-12">
        Last reviewed: 25 April 2026.
      </p>
    </main>
  </div>
);

const Section = ({ title, children }) => (
  <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
    <h2 className="text-base sm:text-lg font-semibold mb-3" style={{ color: '#1A1209' }}>{title}</h2>
    {children}
  </section>
);

export default AccessibilityPage;
